import { api } from "../adapters/api";

const resource: string = "api/v1/events";

export interface CreateEventRequest {
    title: string;
    description?: string;
    dateFrom: string;
    dateTo: string;
    address?: string;
    cityId?: number;
    imageUrl: string;
}

export interface EventResponse {
    id: string;
    title: string;
    description?: string;
    dateFrom: string;
    dateTo: string;
    address?: string;
    cityId?: number;
    cityName?: string;
    countyCode?: string;
    employerName?: string;
    employerId?: string;
    imageUrl?: string;
    enabled?: boolean;
    expiresAt?: string;
    promoted?: boolean;
    promotedUntil?: string;
    lastNotifiedAt?: string;
}

export interface NotifyReach {
    reach: number;
    cost: number;
}

export const isEventDateExpired = (ev: EventResponse): boolean => {
    const end = ev.expiresAt ?? ev.dateTo;
    return end ? new Date(end).getTime() < Date.now() : false;
};

export const isEventExpired = (ev: EventResponse): boolean => {
    if (ev.enabled === false) return true;
    return isEventDateExpired(ev);
};

const DATE_OPTS: Intl.DateTimeFormatOptions = { day: "2-digit", month: "short", year: "numeric" };
const TIME_OPTS: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" };

// "15 mai 2026, 21:00–23:00" pentru aceeași zi · "15 mai 2026, 21:00 — 16 mai 2026, 09:00" pentru zile diferite
// fără ore dacă ambele sunt la miezul nopții (nu a fost setată oră)
export const formatEventRange = (fromStr: string, toStr: string): string => {
    const from = new Date(fromStr);
    const to = new Date(toStr);
    const fromDate = from.toLocaleDateString("ro-RO", DATE_OPTS);
    const toDate = to.toLocaleDateString("ro-RO", DATE_OPTS);
    const fromTime = from.toLocaleTimeString("ro-RO", TIME_OPTS);
    const toTime = to.toLocaleTimeString("ro-RO", TIME_OPTS);
    const hasTime = !(from.getHours() === 0 && from.getMinutes() === 0 && to.getHours() === 0 && to.getMinutes() === 0);
    if (fromDate === toDate) {
        return hasTime ? `${fromDate}, ${fromTime}–${toTime}` : fromDate;
    }
    return hasTime ? `${fromDate}, ${fromTime} — ${toDate}, ${toTime}` : `${fromDate} — ${toDate}`;
};

const authHeader = () => ({ "Authorization": "Bearer " + localStorage.getItem("jwtToken") });

export const createEvent = async (body: CreateEventRequest): Promise<EventResponse> => {
    const res = await api.post(resource, JSON.stringify(body), { ...authHeader(), "Content-Type": "application/json" });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const e: any = new Error(err.message || "Eroare la creare eveniment");
        e.status = res.status;
        e.code = err.errorCode || err.code;
        throw e;
    }
    return res.json();
};

export const getMyEvents = (): Promise<EventResponse[]> =>
    api.get(resource + "/employer", authHeader()).then((res: any) => res.json());

export const presignUpload = (extension: string) =>
    api.post(`api/v1/media/presign?extension=${extension}`, null, authHeader()).then((res: any) => res.json());

export const updateEvent = async (id: string, body: Partial<CreateEventRequest>): Promise<EventResponse> => {
    const res = await api.patch(resource + "/" + id, JSON.stringify(body), { ...authHeader(), "Content-Type": "application/json" });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const e: any = new Error(err["error-message"] || err.message || "Eroare la actualizarea evenimentului");
        e.status = res.status;
        e.code = err["error-code"] || err.errorCode || err.code;
        throw e;
    }
    return res.json();
};

export const setEventEnabled = async (id: string, enabled: boolean): Promise<EventResponse> => {
    const res = await api.patch(resource + "/" + id + "/status?enabled=" + enabled, null, authHeader());
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const e: any = new Error(err["error-message"] || err.message || "Eroare la actualizarea evenimentului");
        e.status = res.status;
        e.code = err["error-code"] || err.errorCode || err.code;
        throw e;
    }
    return res.json();
};

export const deleteEvent = async (id: string): Promise<void> => {
    const res = await api.delete(resource + "/" + id, authHeader());
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const e: any = new Error(err["error-message"] || err.message || "Eroare la ștergerea evenimentului");
        e.status = res.status;
        e.code = err["error-code"] || err.errorCode || err.code;
        throw e;
    }
};

// aruncă eroare cu .status/.code (ex. credite insuficiente) ca să putem redirecta spre /credits
const parseOrThrow = async (res: any, fallback: string) => {
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const e: any = new Error(err["error-message"] || err.message || fallback);
        e.status = res.status;
        e.code = err["error-code"] || err.errorCode || err.code;
        throw e;
    }
    return res.json();
};

export const promoteEvent = async (id: string, durationDays: number): Promise<EventResponse> => {
    const res = await api.patch(resource + "/" + id + "/promote", JSON.stringify({ durationDays }), { ...authHeader(), "Content-Type": "application/json" });
    return parseOrThrow(res, "Eroare la promovarea evenimentului");
};

export const getNotifyReach = (id: string): Promise<NotifyReach> =>
    api.get(resource + "/" + id + "/notify-reach", authHeader()).then((res: any) => res.json());

export const notifyCandidates = async (id: string): Promise<NotifyReach> => {
    const res = await api.post(resource + "/" + id + "/notify", null, authHeader());
    return parseOrThrow(res, "Eroare la trimiterea notificării");
};

export const republishEvent = async (id: string, body: Partial<CreateEventRequest>): Promise<EventResponse> => {
    const res = await api.patch(resource + "/" + id + "/republish", JSON.stringify(body), { ...authHeader(), "Content-Type": "application/json" });
    return parseOrThrow(res, "Eroare la republicarea evenimentului");
};

export const uploadToR2 = async (uploadUrl: string, file: File): Promise<void> => {
    const res = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/octet-stream" },
    });
    if (!res.ok) {
        throw new Error("Încărcarea imaginii a eșuat. Încearcă din nou.");
    }
};
